import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';

const FontSizeContext = createContext();

const MIN_FONT_SCALE = 0.8;
const MAX_FONT_SCALE = 1.4;
const STEP_SIZE = 0.1;

const BASE_FONT_SIZES = {
  'xs': '0.75rem',
  'sm': '0.875rem',
  'md': '1rem',
  'lg': '1.125rem',
  'xl': '1.25rem',
  '2xl': '1.5rem',
  '3xl': '1.875rem',
  '4xl': '2.25rem',
  '5xl': '3rem',
  '6xl': '3.75rem',
  '7xl': '4.5rem',
  '8xl': '6rem',
  '9xl': '8rem',
};

export const FontSizeProvider = ({ children }) => {
  const [fontScale, setFontScale] = useState(() => {
    const savedScale = localStorage.getItem('kanbanFontScale');
    return savedScale ? parseFloat(savedScale) : 1;
  });

  useEffect(() => {
    localStorage.setItem('kanbanFontScale', fontScale.toString());
  }, [fontScale]);

  const scaledFontSizes = useMemo(() => {
    const scaled = {};
    Object.entries(BASE_FONT_SIZES).forEach(([key, value]) => {
      const baseSize = parseFloat(value);
      scaled[key] = `${baseSize * fontScale}rem`;
    });
    return scaled;
  }, [fontScale]);

  const increaseFontSize = () => {
    setFontScale(prev => Math.min(prev + STEP_SIZE, MAX_FONT_SCALE));
  };

  const decreaseFontSize = () => {
    setFontScale(prev => Math.max(prev - STEP_SIZE, MIN_FONT_SCALE));
  };

  const resetFontSize = () => {
    setFontScale(1);
  };

  return (
    <FontSizeContext.Provider value={{ 
      fontScale, 
      increaseFontSize, 
      decreaseFontSize, 
      resetFontSize,
      fontSize: scaledFontSizes 
    }}>
      {children}
    </FontSizeContext.Provider>
  );
};

export const useFontSize = () => {
  const context = useContext(FontSizeContext);
  if (!context) {
    throw new Error('useFontSize must be used within a FontSizeProvider');
  }
  return context;
};
